import React, { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useGlobalStore } from "@/GlobalStore";
import { AdCreative } from "@/screens/creative-library/types";
import { ChevronDown, Plus, X, Check } from "lucide-react";
import { updateImageLogoPosition } from "@/screens/creative-library/apiService";
import { uploadFilesAsync } from "@/api/globalApiService";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { LuCircleCheck, LuTriangleAlert } from "react-icons/lu";
import LoadingSpinner from "@/components/common/loaders/LoadingSpinner";
import { updateAccountLogos } from "@/api/globalApiService";
import { Logo } from "@/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconCircleOff } from "@tabler/icons-react";
import { Image } from "@/screens/creatives/types";

interface EditLogoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creative: AdCreative;
  onSave: (image: Image) => void;
}

interface SelectionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const MAXIMUM_LOGOS = 5;

const EditLogoDialog: React.FC<EditLogoDialogProps> = ({
  open,
  onOpenChange,
  creative,
  onSave,
}) => {
  const { selectedAccount, setSelectedAccount } = useGlobalStore();
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(
    creative.creative.configuration?.logo_file_id || null,
  );
  const [logos, setLogos] = useState<Logo[]>(selectedAccount?.logos || []);
  const [isLogoUploading, setIsLogoUploading] = useState<boolean>(false);
  const [isUpdatingLogos, setIsUpdatingLogos] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const imageRef = useRef<HTMLImageElement>(null);
  const [showResizeHandles, setShowResizeHandles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [scaleFactor, setScaleFactor] = useState<number>(1);
  const [isSaving, setIsSaving] = useState(false);

  const imageUrl = creative.creative.configuration?.image_url;
  const logoCoordinates = creative.creative.configuration?.logo_coordinates;

  // Validate selectedLogoId and apply fallback logic
  React.useEffect(() => {
    if (selectedLogoId === null) return;

    // Check if the currently selected logo exists in the account logos
    const logoExists = logos.some((logo) => logo.file_id === selectedLogoId);

    if (!logoExists) {
      // If selected logo doesn't exist, fallback to first available logo
      if (logos.length > 0) {
        setSelectedLogoId(logos[0]?.file_id);
      } else {
        setSelectedLogoId(null);
      }
      toast.message(
        <ToastContent
          icon={LuTriangleAlert}
          headingPrefix="Original logo no longer available"
          subheadingSuffix="Swapped in another logo to keep layout intact. You can choose a different one anytime."
          isError={true}
        />,
      );
    }
  }, [logos, selectedLogoId]);

  // Initialize selection box - will be updated when image loads with proper scaling
  const [selectionBox, setSelectionBox] = useState<SelectionBox>(() => {
    return { x: 50, y: 50, width: 150, height: 100 }; // Default selection box in pixels
  });

  // Handle image load to calculate scale factor and set initial coordinates
  const handleImageLoad = useCallback(() => {
    if (!imageRef.current) return;

    const naturalWidth = imageRef.current.naturalWidth;
    const naturalHeight = imageRef.current.naturalHeight;
    const displayedWidth = imageRef.current.clientWidth;
    const displayedHeight = imageRef.current.clientHeight;

    // Validate dimensions before calculating scale
    if (!naturalWidth || !naturalHeight || !displayedWidth || !displayedHeight) {
      console.warn("Invalid image dimensions, skipping scale calculation");
      return;
    }

    // Calculate scale factor (from original to displayed)
    const scaleX = displayedWidth / naturalWidth;
    const scaleY = displayedHeight / naturalHeight;
    const scale = Math.min(scaleX, scaleY); // Use the smaller scale to maintain aspect ratio

    // Validate scale factor
    if (!isFinite(scale) || scale <= 0) {
      console.warn("Invalid scale factor:", scale);
      return;
    }

    setScaleFactor(scale);

    // If we have logo coordinates, scale them down to displayed size
    if (logoCoordinates) {
      // Validate logo coordinates
      const { x1, y1, x2, y2 } = logoCoordinates;
      if (
        typeof x1 !== "number" ||
        typeof y1 !== "number" ||
        typeof x2 !== "number" ||
        typeof y2 !== "number" ||
        !isFinite(x1) ||
        !isFinite(y1) ||
        !isFinite(x2) ||
        !isFinite(y2)
      ) {
        console.warn("Invalid logo coordinates:", logoCoordinates);
        return;
      }

      const scaledCoordinates = {
        x: x1 * scale,
        y: y1 * scale,
        width: (x2 - x1) * scale,
        height: (y2 - y1) * scale,
      };

      // Validate scaled coordinates before setting
      if (Object.values(scaledCoordinates).every((val) => isFinite(val) && val >= 0)) {
        setSelectionBox(scaledCoordinates);
      } else {
        console.warn("Invalid scaled coordinates:", scaledCoordinates);
      }
    }
  }, [logoCoordinates]);

  const selectedLogo = logos.find((logo) => logo.file_id === selectedLogoId);

  const getRelativePosition = useCallback((clientX: number, clientY: number) => {
    if (!imageRef.current) return { x: 0, y: 0 };

    const rect = imageRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    return { x, y };
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, type: "move" | "resize", handle?: string) => {
      e.preventDefault();
      e.stopPropagation();

      const pos = getRelativePosition(e.clientX, e.clientY);

      if (type === "move") {
        setIsDragging(true);
        setDragStart({ x: pos.x - selectionBox.x, y: pos.y - selectionBox.y });
      } else if (type === "resize") {
        setIsResizing(true);
        setResizeHandle(handle || null);
        setDragStart(pos);
      }
    },
    [getRelativePosition, selectionBox],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging && !isResizing) return;
      if (!imageRef.current) return;

      const pos = getRelativePosition(e.clientX, e.clientY);
      const imageRect = imageRef.current.getBoundingClientRect();

      if (isDragging) {
        const newX = Math.max(
          0,
          Math.min(imageRect.width - selectionBox.width, pos.x - dragStart.x),
        );
        const newY = Math.max(
          0,
          Math.min(imageRect.height - selectionBox.height, pos.y - dragStart.y),
        );

        setSelectionBox((prev) => ({ ...prev, x: newX, y: newY }));
      } else if (isResizing && resizeHandle) {
        setSelectionBox((prev) => {
          const newBox = { ...prev };

          switch (resizeHandle) {
            case "nw":
              newBox.width = Math.max(20, prev.width + (prev.x - pos.x));
              newBox.height = Math.max(20, prev.height + (prev.y - pos.y));
              newBox.x = Math.max(0, pos.x);
              newBox.y = Math.max(0, pos.y);
              break;
            case "ne":
              newBox.width = Math.max(20, pos.x - prev.x);
              newBox.height = Math.max(20, prev.height + (prev.y - pos.y));
              newBox.y = Math.max(0, pos.y);
              break;
            case "sw":
              newBox.width = Math.max(20, prev.width + (prev.x - pos.x));
              newBox.height = Math.max(20, pos.y - prev.y);
              newBox.x = Math.max(0, pos.x);
              break;
            case "se":
              newBox.width = Math.max(20, pos.x - prev.x);
              newBox.height = Math.max(20, pos.y - prev.y);
              break;
          }

          // Ensure the box stays within bounds
          if (newBox.x + newBox.width > imageRect.width) {
            newBox.width = imageRect.width - newBox.x;
          }
          if (newBox.y + newBox.height > imageRect.height) {
            newBox.height = imageRect.height - newBox.y;
          }

          return newBox;
        });
      }
    },
    [isDragging, isResizing, dragStart, selectionBox, resizeHandle, getRelativePosition],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const handleSave = async () => {
    if (!imageRef.current || scaleFactor === 0 || !selectedAccount?.id) return;

    // Convert displayed pixel coordinates back to original image pixel coordinates
    // We need to scale UP by dividing by the scale factor
    const originalCoordinates = {
      x1: Math.round(selectionBox.x / scaleFactor),
      y1: Math.round(selectionBox.y / scaleFactor),
      x2: Math.round((selectionBox.x + selectionBox.width) / scaleFactor),
      y2: Math.round((selectionBox.y + selectionBox.height) / scaleFactor),
    };

    setIsSaving(true);

    try {
      const response = await updateImageLogoPosition(
        selectedAccount.id,
        creative.creative.images[0].id,
        { logo_file_id: selectedLogoId, logo_coordinates: originalCoordinates },
      );

      if (response.status === "success") {
        toast.message(
          <ToastContent
            icon={LuCircleCheck}
            headingPrefix="Logo position updated successfully."
            subheadingSuffix="The logo has been positioned on your creative."
            isError={false}
          />,
        );
        onOpenChange(false);
        onSave(response.data);
      } else {
        throw new Error("Failed to update logo position");
      }
    } catch (error) {
      console.error("Error updating logo position:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0 && selectedAccount?.id) {
      const remainingSlots = MAXIMUM_LOGOS - logos.length;
      const files = Array.from(event.target.files).slice(0, remainingSlots);

      setIsLogoUploading(true);
      try {
        const response = await uploadFilesAsync(files, selectedAccount?.id || "");
        if (response.files?.length) {
          const newLogos: Logo[] = response.files.map((file, index) => ({
            file_id: file.id,
            url: file.public_url,
            type: files[index].type,
          }));
          const updatedLogos = [...logos, ...newLogos];
          setLogos(updatedLogos);

          // Update the account logos on the server
          await updateAccountLogos(
            selectedAccount.id,
            updatedLogos.map((logo) => logo.file_id),
          );

          // Update the global store
          setSelectedAccount({ ...selectedAccount, logos: updatedLogos });

          toast.message(
            <ToastContent
              icon={LuCircleCheck}
              headingPrefix="Logos uploaded successfully"
              subheadingSuffix="New logos have been added to your account."
              isError={false}
            />,
          );
        }
      } catch (error) {
        console.error("Error uploading logos:", error);
      } finally {
        setIsLogoUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleRemoveLogo = async (logoToRemove: Logo) => {
    if (!selectedAccount?.id) return;

    setIsUpdatingLogos(true);
    try {
      const updatedLogos = logos.filter((logo) => logo.file_id !== logoToRemove.file_id);
      setLogos(updatedLogos);

      // If the removed logo was selected, clear the selection
      if (selectedLogoId === logoToRemove.file_id) {
        setSelectedLogoId(null);
      }

      // Update the account logos on the server
      await updateAccountLogos(
        selectedAccount.id,
        updatedLogos.map((logo) => logo.file_id),
      );

      // Update the global store
      setSelectedAccount({ ...selectedAccount, logos: updatedLogos });

      toast.message(
        <ToastContent
          icon={LuCircleCheck}
          headingPrefix="Logo removed successfully"
          subheadingSuffix="The logo has been removed from your account."
          isError={false}
        />,
      );
    } catch (error) {
      console.error("Error removing logo:", error);
    } finally {
      setIsUpdatingLogos(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="top-0 translate-y-[31px] gap-0 p-0 w-max max-w-2xl bg-transparent border-none shadow-none"
        onClick={(e) => {
          // Check if click is outside the selection box
          const selectionBoxElement =
            e.currentTarget.querySelector("[data-selection-box]");
          if (selectionBoxElement && !selectionBoxElement.contains(e.target as Node)) {
            setShowResizeHandles(false);
          }
        }}
        onInteractOutside={(e) => {
          e.preventDefault();
          setShowResizeHandles(false);
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center w-[627px] p-2 bg-white rounded-[12px]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-[6px]">
                <ChevronDown className="text-brand-primary-600" size={16} />
                <span className="font-bold">Change Logo</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={20}
              className="w-auto p-4 min-w-[320px]"
            >
              <div className="flex flex-wrap gap-3 mb-4">
                {logos.map((logo, index) => (
                  <div
                    key={`${logo.file_id}-${index}`}
                    className="relative border border-border-gray group rounded-[8px] cursor-pointer"
                    onClick={() => setSelectedLogoId(logo.file_id)}
                  >
                    <img
                      src={logo.url}
                      alt={`Logo ${index + 1}`}
                      className="h-[82px] w-[82px] object-cover rounded-[8px]"
                      loading="lazy"
                    />

                    {/* Selected indicator */}
                    {selectedLogoId === logo.file_id && (
                      <div className="absolute h-full w-full top-0 left-0 bg-[#09090B66] rounded-[8px] flex items-center justify-center">
                        <div className="flex w-[36px] h-[36px] justify-center items-center rounded-[8px] bg-[#09090B80]">
                          <Check size={26} color="white" strokeWidth={2} />
                        </div>
                      </div>
                    )}

                    {/* Remove button */}
                    <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Button
                        className="h-5 w-5 p-0 bg-[#09090B80] hover:bg-[#09090B] border-0 rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleRemoveLogo(logo);
                        }}
                        disabled={isUpdatingLogos}
                      >
                        <X size={12} strokeWidth={2} color="#fff" className="shrink-0" />
                      </Button>
                    </div>
                  </div>
                ))}
                {/* None option */}
                <div
                  className="relative w-[82px] h-[82px] flex justify-center items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 rounded-[8px] border border-border-gray"
                  onClick={() => setSelectedLogoId(null)}
                >
                  <div className="flex flex-col items-center gap-[2px]">
                    <IconCircleOff
                      size={18}
                      className="text-brand-primary-600"
                      strokeWidth={2}
                    />
                    <span className="text-sm text-brand-primary-600 font-bold">None</span>
                  </div>
                  {/* Selected indicator */}
                  {selectedLogoId === null && (
                    <div className="absolute h-full w-full top-0 left-0 bg-[#09090B66] rounded-[8px] flex items-center justify-center">
                      <div className="flex w-[36px] h-[36px] justify-center items-center rounded-[8px] bg-[#09090B80]">
                        <Check size={26} color="white" strokeWidth={2} />
                      </div>
                    </div>
                  )}
                </div>
                {/* Upload button */}
                {logos.length < MAXIMUM_LOGOS && (
                  <label
                    htmlFor="logo-upload"
                    className="flex items-center justify-center w-[82px] h-[82px] rounded-[8px] bg-[#F6F6F6] cursor-pointer border border-border-gray hover:bg-[#EEEEEE] transition-colors"
                    tabIndex={0}
                  >
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        void handleLogoUpload(e);
                      }}
                      ref={fileInputRef}
                      disabled={isLogoUploading}
                      multiple
                    />
                    {isLogoUploading ? (
                      <LoadingSpinner size={26} color="#FF4500" />
                    ) : (
                      <Plus size={26} strokeWidth={1.4} color="#1F3861" />
                    )}
                  </label>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="font-bold"
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={() => void handleSave()}
              disabled={isSaving}
              className="font-bold"
            >
              {isSaving ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size={14} color="#fff" />
                  <span>Saving...</span>
                </div>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="mt-[65px] flex justify-center">
          <div className="w-max relative rounded-[8px] overflow-hidden">
            {imageUrl && (
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Creative"
                className="h-[526px] w-auto object-contain"
                draggable={false}
                onLoad={handleImageLoad}
              />
            )}

            {/* Selection Box Overlay */}
            <div
              data-selection-box
              className="absolute border-2 rounded-[8px] border-white cursor-move"
              style={{
                left: `${selectionBox.x}px`,
                top: `${selectionBox.y}px`,
                width: `${selectionBox.width}px`,
                height: `${selectionBox.height}px`,
                display: selectedLogoId ? "block" : "none",
              }}
              onMouseDown={(e) => {
                setShowResizeHandles(true);
                handleMouseDown(e, "move");
              }}
            >
              {/* Logo Preview inside selection box */}
              {selectedLogo && (
                <img
                  src={selectedLogo.url}
                  alt="Logo"
                  className="w-full h-full object-contain opacity-80 rounded-[8px]"
                  draggable={false}
                />
              )}

              {/* Resize Handles */}
              {[
                { pos: "nw", style: { top: "-6px", left: "-6px", cursor: "nw-resize" } },
                { pos: "ne", style: { top: "-6px", right: "-6px", cursor: "ne-resize" } },
                {
                  pos: "sw",
                  style: { bottom: "-6px", left: "-6px", cursor: "sw-resize" },
                },
                {
                  pos: "se",
                  style: { bottom: "-6px", right: "-6px", cursor: "se-resize" },
                },
              ].map(({ pos, style }) => (
                <div
                  key={pos}
                  className={`absolute w-[12px] h-[12px] bg-white  rounded-full ${
                    showResizeHandles ? "opacity-100" : "opacity-0"
                  }`}
                  style={style}
                  onMouseDown={(e) => handleMouseDown(e, "resize", pos)}
                />
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditLogoDialog;
